import type { PropsWithChildren } from 'react';
import React, { memo, useMemo } from 'react';

import type { VehicleListQuery } from 'Components/Account/AccountPage.types';
import type { AccountCategory, AccountSlugs } from 'Types/account';
import type { PropsWithRequiredChildren } from 'Types/react/react';

export const enum FILTER_VIEWS {
	DEFAULT = 'default',
	PURCHASES_COMPLETE = 'purchasesComplete',
}

export type AccountFiltersSwitchProps = {
	breakpointDesktopAccount?: boolean;
	category: AccountCategory;
	currentCount?: number;
	slug: AccountSlugs;
} & Pick<VehicleListQuery, 'queryStatus' | 'isPreviousData'>;

const AccountFiltersViewContext = React.createContext(FILTER_VIEWS.DEFAULT);

const useAccountFiltersViewContext = () => {
	const context = React.useContext(AccountFiltersViewContext);
	if (!context) {
		throw new Error(
			'Account Filters compound components cannot be rendered outside the AccountFiltersSwitch component',
		);
	}
	return context;
};

export const PurchasesComplete: React.FC<PropsWithRequiredChildren> = ({ children }) => {
	const view = useAccountFiltersViewContext();
	return view === FILTER_VIEWS.PURCHASES_COMPLETE ? <>{children}</> : null;
};

export const Default: React.FC<PropsWithRequiredChildren> = ({ children }) => {
	const view = useAccountFiltersViewContext();
	return view === FILTER_VIEWS.DEFAULT ? <>{children}</> : null;
};

export const getViewFromCategoryAndSlug = (category: string, slug: string): FILTER_VIEWS => {
	let view = FILTER_VIEWS.DEFAULT;

	if (category === 'purchases' && slug === 'complete') {
		view = FILTER_VIEWS.PURCHASES_COMPLETE;
	}

	return view;
};

const AccountFiltersSwitch: React.FC<PropsWithChildren<AccountFiltersSwitchProps>> = ({ category, children, slug }) => {
	const view = useMemo(() => getViewFromCategoryAndSlug(category, slug), [category, slug]);

	return <AccountFiltersViewContext.Provider value={view}>{children}</AccountFiltersViewContext.Provider>;
};

const comparer = (
	{
		category: prevCategory,
		currentCount: prevCurrentCount,
		isPreviousData: prevIsPreviousData,
		queryStatus: prevQueryStatus,
		slug: prevSlug,
	}: AccountFiltersSwitchProps,
	{ category, currentCount, isPreviousData, queryStatus, slug }: AccountFiltersSwitchProps,
): boolean =>
	prevCategory === category &&
	prevSlug === slug &&
	prevCurrentCount === currentCount &&
	queryStatus === prevQueryStatus &&
	isPreviousData === prevIsPreviousData;

type AccountFiltersSwitchMemoType = React.NamedExoticComponent<React.PropsWithChildren<AccountFiltersSwitchProps>>;
const AccountFiltersSwitchMemo: AccountFiltersSwitchMemoType = memo(AccountFiltersSwitch, comparer);

export default AccountFiltersSwitchMemo;
